import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { Controller, Custom, DateTime, Progress } from "components";
import { useApp } from "context";
import { PairValue } from "types";
import { Constants, Enums, Helpers, Methods, Schedule } from "utils";
import { IQarePlan, IService } from "interfaces";

type Props = {
  services: any;
  serviceId: number;
  isWalkIn: boolean;
  service: IService;
  clinicId: number | "";
  qarePlan?: IQarePlan;
  onSubmit?: () => void;
  reasons: PairValue[];
  clinics: PairValue[];
  healthGoals: PairValue[];
  onDone?: (state: boolean) => void;
  onError?: (message?: string) => void;
};

export const Appointment = (props: Props) => {
  const { user, t, theme, service } = useApp();
  const [clinicId, setClinicId] = useState<number | "">(props.clinicId);
  const [providerId, setProviderId] = useState<number | "">("");
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [slots, setSlots] = useState<Custom.ChipProps[] | null>(null);
  const [providers, setProviders] = useState<PairValue[]>([]);
  const [date, setDate] = useState<Date | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [reason, setReason] = useState<string>("");
  const [healthGoals, setHealthGoals] = useState<number[]>([]);

  const isCalendarDisabled =
    clinicId === "" || providerId === "" || !Helpers.hasValue(calendarId);

  const isAvailabilityDisabled = date === null;
  const isSubmitButtonDisabled =
    isCalendarDisabled || isAvailabilityDisabled || slot === null;

  const hasDropdown = [Enums.EnumService.MentalHealth].includes(
    props.serviceId
  );

  React.useEffect(
    () => handleSelectClinic(Number(props.clinicId) || props.clinicId),
    [props.clinicId]
  );

  async function fetchProviders(clinicId: number) {
    const response = await service.provider.findByClinicAndService(
      Number(clinicId),
      props.serviceId
    );
    setClinicId(clinicId);
    setProviders(response);
    props.qarePlan && handleSelectProvider(props.qarePlan.providerId);
  }

  async function fetchSlots(selected: Date) {
    const response = await service.google.availability(
      String(calendarId),
      selected,
      Constants.APPOINTMENT_DURATION,
      props.service.startTime,
      props.service.endTime
    );
    setDate(selected);
    setSlots(response);
  }

  function handleSelectClinic(clinicId: number | string): void {
    if (!Helpers.isNumber(clinicId)) return;
    setDate(null);
    setSlot(null);
    fetchProviders(clinicId as number);
  }

  async function handleSelectProvider(value: number) {
    // const response = await service.calendar.find(Number(clinicId), value);
    // if (!Helpers.hasValue(response)) {
    //   props.onError && props.onError(t.message.calendar_not_found);
    // }
    setProviderId(value);
    setCalendarId(Constants.CALENDAR_ID);
    setDate(null);
    setSlot(null);
  }

  function handleSelectReason(value: number): void {
    const values = Object.values(props.services);
    const reason = t.reason[values[value] as string];
    setReason(reason);
  }

  function handleTextareaReason(
    event: React.FocusEvent<HTMLInputElement>
  ): void {
    setReason(event.target.value);
  }

  function handleAutocomplete(
    _: React.SyntheticEvent<Element, Event>,
    items: PairValue[] | null
  ): void {
    setHealthGoals(items?.map((item: PairValue) => item.key) ?? []);
  }

  function handleSelectDate(selected: Date): void {
    fetchSlots(selected);
  }

  function handleSelectSlot(value: string): void {
    setSlot(value);
  }

  async function handleAppointmentSubmit() {
    if (!isSubmitButtonDisabled) {
      Methods.makeAppointment(
        service,
        Number(clinicId),
        Number(providerId),
        user.userId,
        String(calendarId),
        props.serviceId,
        date,
        slot,
        reason,
        healthGoals,
        false,
        false,
        props.onDone,
        props.onError
      );
    }
  }

  return (
    <Box sx={{ minHeight: 400, position: "relative" }}>
      {props.clinics.length > 0 ? (
        <>
          <Grid container spacing={theme.margin.sm}>
            <Grid item xs={12} sm={6}>
              <Custom.Select
                label={t.label.clinic}
                items={props.clinics}
                onChange={handleSelectClinic}
                value={clinicId}
                disabled={props.isWalkIn}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Custom.Select
                label={t.label.provider}
                items={providers}
                value={providerId}
                onChange={handleSelectProvider}
              />
            </Grid>
            <Grid item xs={12}>
              {hasDropdown ? (
                <Custom.Select
                  label={t.label.reason}
                  items={props.reasons}
                  onChange={handleSelectReason}
                />
              ) : (
                <Custom.TextField
                  multiline={true}
                  label={t.label.reason}
                  onBlur={handleTextareaReason}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Custom.Autocomplete
                label={t.label.health_goal}
                items={props.healthGoals}
                multiple
                onChange={handleAutocomplete}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={theme.margin.md}
            sx={{ marginTop: theme.margin.md }}
          >
            <Grid item xs={12} sm={6}>
              <DateTime.Calendar
                weekend={true}
                min={new Date()}
                rules={Constants.DEFAULT_SCHEDULE}
                exceptions={Schedule.EXCEPTION_SCHEDULE}
                onSelect={handleSelectDate}
                disabled={isCalendarDisabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: "flex",
                  minHeight: "100%",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    marginBottom: theme.margin.md,
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: theme.font.md,
                      fontWeight: theme.font.light,
                      color: theme.palette.font.color,
                    }}
                  >
                    {t.label.availability}
                  </Typography>
                </Box>
                <Box sx={{ position: "relative", height: "inherit" }}>
                  {slots ? (
                    slots.length > 0 ? (
                      <Controller.Chip
                        sx={{
                          color: theme.palette.font.color,
                          backgroundColor: theme.palette.background.color,
                        }}
                        disabled={isAvailabilityDisabled}
                        items={slots}
                        onSelect={handleSelectSlot}
                      />
                    ) : (
                      <Progress.NoRecord
                        height={200}
                        message={t.message.placeholder.no_slot_available}
                      />
                    )
                  ) : (
                    <Progress.NoRecord height={200} />
                  )}
                </Box>
                <Box marginTop="auto" display="flex" justifyContent="flex-end">
                  <Custom.Button
                    size="large"
                    disabled={isSubmitButtonDisabled}
                    onClick={handleAppointmentSubmit}
                  >
                    {t.action.submit}
                  </Custom.Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      ) : (
        <Progress.PageProcess />
      )}
    </Box>
  );
};
